import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import Grid from '@material-ui/core/Grid';
import { get } from 'lodash';
import { useTranslation } from '../../src/i18n';
import { colors, fontSize } from '../../constants/styleConstants';
import { TP } from '../../constants/consts';
import CustomChips from '../CustomChips';
import { getChipsForProductionCard, getRoutePartWithSlug, getRouteWithSlug } from '../../utils/common';
import route from '../../constants/routes';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'inline-flex',
    flexWrap: 'wrap',
    '& > *': {
      marginRight: theme.spacing(0.5),
    },
  },
  chip: {
    fontSize: fontSize.regularTitle,
    color: colors.white,
  },
}));

const PerformanceChips = ({
  className,
  performanceTypes,
  type = 'small',
  chipClassName,
  isCoProduced,
  coProductions,
  festivals,
  checkPermissions,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [
    isWorldPremier,
    isNationalPremier,
    isConcert,
    isNewProduction,
    isCancelled,
    isPartOfFestival,
    doubleTripleBill,
    isRectical,
  ] = getChipsForProductionCard(performanceTypes);
  if (
    !isWorldPremier &&
    !isNationalPremier &&
    !isNewProduction &&
    !isPartOfFestival &&
    !isRectical &&
    !doubleTripleBill &&
    !isCoProduced &&
    !isConcert
  ) {
    return null;
  }

  return (
    <Grid item className={classnames(classes.root, className)}>
      {isWorldPremier !== false && (
        <CustomChips
          type={type}
          size="small"
          label="WP"
          content={t(`${TP}.FN_PERFORMACE_TYPE_WORLD_PREMIERE`)}
          className={classnames(classes.chip, chipClassName)}
        />
      )}
      {isNationalPremier && (
        <CustomChips
          type={type}
          size="small"
          label="NP"
          content={t(`${TP}.FN_PERFORMACE_TYPE_NATIONAL_PREMIERE`)}
          className={classnames(classes.chip, chipClassName)}
        />
      )}
      {isNewProduction && (
        <CustomChips
          type={type}
          size="small"
          label="N"
          content={t(`${TP}.FN_PERFORMACE_TYPE_NEW_PRODUCTION`)}
          className={classnames(classes.chip, chipClassName)}
        />
      )}
      {isCoProduced &&
        (coProductions?.length > 0 ? (
          coProductions.map(company => (
            <CustomChips
              type={type}
              size="small"
              label="CP"
              linkProps={{
                href: `${route.COMPANIES}/[country]/[id]/[[...action]]`,
                as: getRouteWithSlug(`${route.COMPANIES}/${get(company, 'country.slug')}`, company),
                target: '_blank',
              }}
              linkName={company?.name}
              content={t(`${TP}.FN_CO_PRODUCED`)}
              isArchived={company?.id === -1}
              checkPermissions={checkPermissions}
              className={classnames(classes.chip, chipClassName)}
            />
          ))
        ) : (
          <CustomChips
            type={type}
            size="small"
            label="CP"
            content={t(`${TP}.FN_CO_PRODUCED`)}
            className={classnames(classes.chip, chipClassName)}
          />
        ))}
      {isPartOfFestival &&
        (festivals?.length > 0 ? (
          festivals.map(festival => (
            <CustomChips
              type={type}
              size="small"
              label="PF"
              linkProps={{
                target: '_blank',
                href: `${route.FESTIVALS}/[year]/[country]/[id]/[[...action]]`,
                as: `${route.FESTIVALS}/${get(festival, 'year')}/${get(
                  festival,
                  'country.slug',
                )}/${getRoutePartWithSlug(festival)}`,
              }}
              isArchived={festival?.id === -1}
              linkName={festival?.name}
              checkPermissions={checkPermissions}
              content={t(`${TP}.FN_PERFORMACE_TYPE_PART_OF_FESTIVAL`)}
              className={classnames(classes.chip, chipClassName)}
            />
          ))
        ) : (
          <CustomChips
            type={type}
            size="small"
            label="PF"
            content={t(`${TP}.FN_PERFORMACE_TYPE_PART_OF_FESTIVAL`)}
            className={classnames(classes.chip, chipClassName)}
          />
        ))}
      {/* {liveEvent && (
        <CustomChips
        type={type}
          size="small"
          label="L"
          content={t(`${TP}.FN_PERFORMACE_TYPE_CONCERT`)}
          className={classnames(classes.chip, classes.concert)}
        />
      )} */}
      {doubleTripleBill && (
        <CustomChips
          type={type}
          size="small"
          label="DB"
          content={t(`${TP}.FN_DOUBLE_TRIPLE_BILL`)}
          className={classnames(classes.chip, chipClassName)}
        />
      )}
      {isConcert && (
        <CustomChips
          type={type}
          size="small"
          label="C"
          content={t(`${TP}.FN_PERFORMACE_TYPE_CONCERT`)}
          className={classnames(classes.chip, chipClassName)}
        />
      )}
      {isRectical && (
        <CustomChips
          type={type}
          size="small"
          label="R"
          content={t(`${TP}.FN_RECITAL`)}
          className={classnames(classes.chip, chipClassName)}
        />
      )}
      {/* {digitalOnly && (
        <CustomChips
          type={type}
          size="small"
          label="D"
          content={t(`${TP}.FN_PERFORMACE_TYPE_CONCERT`)}
          className={classnames(classes.chip, classes.concert)}
        />
      )} */}
    </Grid>
  );
};

PerformanceChips.propTypes = {
  className: PropTypes.string,
  type: PropTypes.string,
  performanceTypes: PropTypes.array,
};

export default memo(PerformanceChips);
